import { useState } from "react";
import { ErrorBox, Field, Loading, Toggle, useAction } from "../../components/ui.tsx";
import { useApi } from "../../lib/api.ts";
import { useSettings } from "../../lib/settings.ts";
import type { SystemStatus } from "../../lib/types.ts";

function Check({ ok, label, detail }: { ok: boolean; label: string; detail?: string }) {
  return (
    <div className="row nowrap">
      <i className={`dot ${ok ? "ok" : "bad"}`} />
      <span>{label}</span>
      {detail && <span className="muted small ellipsis">{detail}</span>}
    </div>
  );
}

export function ProjectsRootField({ onSaved }: { onSaved?: () => void }) {
  const settings = useSettings();
  const system = useApi<SystemStatus>("/api/system");
  const [root, setRoot] = useState<string | null>(null);
  const value = root ?? settings.get("server.projects_root", "");
  const [save, saving] = useAction(async () => {
    await settings.save({ "server.projects_root": value.trim() || null });
    setRoot(null);
    await system.mutate();
    onSaved?.();
  }, "Projects root saved");
  const info = system.data?.projectsRoot;
  const example = system.data?.platform === "win32" ? "D:\\projects" : "/srv/projects";
  return (
    <Field
      label="Projects root"
      hint={
        info?.path && root === null
          ? info.exists
            ? info.writable
              ? "Exists and is writable. Projects can only live under this folder."
              : "Exists but is not writable by the server."
            : "This folder does not exist on the server yet. Create it first."
          : "Every project lives under this folder; nothing outside it can be used."
      }
      error={info?.path && root === null && !info.exists ? "Folder not found" : undefined}
    >
      <div className="row nowrap">
        <input className="mono" value={value} placeholder={example} onChange={(e) => setRoot(e.target.value)} />
        <button className="btn primary" disabled={saving || root === null} onClick={() => save()}>
          Save
        </button>
      </div>
    </Field>
  );
}

export function ServerSettings() {
  const settings = useSettings();
  const system = useApi<SystemStatus>("/api/system");
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [githubToken, setGithubToken] = useState("");
  const val = (k: string) => draft[k] ?? settings.get(k, "");
  const [save, saving] = useAction(async () => {
    const values: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(draft)) values[k] = v.trim() || null;
    if (githubToken.trim()) values["github.token"] = githubToken.trim();
    await settings.save(values);
    setDraft({});
    setGithubToken("");
  }, "Saved");
  const [clearToken] = useAction(() => settings.save({ "github.token": null }), "GitHub token removed");

  if (!settings.data) return settings.error ? <ErrorBox error={settings.error} /> : <Loading />;
  const s = system.data;
  const ghSet = settings.get<{ set: boolean }>("github.token", { set: false }).set;
  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Server</h1>
          <div className="sub">Where projects live, how runners start, and the git identity for commits.</div>
        </div>
      </div>

      <section className="card">
        <h2>Host</h2>
        {s ? (
          <div className="grid2">
            <div className="stack tight">
              <Check ok={s.git.installed} label="git" detail={s.git.version} />
              <Check ok={s.gh.installed} label="GitHub CLI (gh)" detail={s.gh.installed ? s.gh.version : "needed for Open PR"} />
              <Check ok={s.claude.installed} label="Claude Code" detail={s.claude.version} />
            </div>
            <div className="stack tight">
              <Check ok={s.tmux} label="tmux" detail={s.tmux ? "available" : "not available; runners start as detached processes"} />
              <div className="muted small">
                Runners: <b>{s.launcher}</b> · {s.platform} · Node {s.node}
              </div>
            </div>
          </div>
        ) : system.error ? (
          <ErrorBox error={system.error} />
        ) : (
          <Loading />
        )}
      </section>

      <section className="card">
        <h2>Projects</h2>
        <ProjectsRootField />
        <Field label="Public URL of this dashboard" hint="Optional. Adds an “Open in dashboard” button to Discord status messages.">
          <input className="mono" value={val("server.public_url")} placeholder="https://switchboard.example.com" onChange={(e) => setDraft({ ...draft, "server.public_url": e.target.value })} />
        </Field>
        <Toggle
          label="Use tmux for runners"
          hint="Each runner gets a window in `tmux attach -t switchboard`. Takes effect on the next server start."
          checked={settings.get("server.use_tmux", true)}
          onChange={(v) => void settings.save({ "server.use_tmux": v })}
        />
        <Toggle
          label="Resume sessions after a restart"
          hint="On boot, sessions that were alive but whose runner is gone start again from their stored Claude session."
          checked={settings.get("server.auto_resume", true)}
          onChange={(v) => void settings.save({ "server.auto_resume": v })}
        />
      </section>

      <section className="card">
        <h2>Git &amp; GitHub</h2>
        <div className="grid2">
          <Field label="Commit author name">
            <input value={val("git.user_name")} placeholder="Switchboard" onChange={(e) => setDraft({ ...draft, "git.user_name": e.target.value })} />
          </Field>
          <Field label="Commit author email">
            <input value={val("git.user_email")} placeholder="you@example.com" onChange={(e) => setDraft({ ...draft, "git.user_email": e.target.value })} />
          </Field>
        </div>
        <Field label="GitHub token" hint={ghSet ? "A token is saved (encrypted). Used for Push and Open PR over HTTPS." : "Fine-grained token with Contents and Pull requests write access. Used for Push and Open PR."}>
          <div className="row nowrap">
            <input type="password" autoComplete="off" className="mono" value={githubToken} placeholder={ghSet ? "•••••••••••• (saved)" : "github_pat_…"} onChange={(e) => setGithubToken(e.target.value)} />
            {ghSet && (
              <button className="btn danger sm" onClick={() => clearToken()}>
                Remove
              </button>
            )}
          </div>
        </Field>
        <div>
          <button className="btn primary" disabled={saving || (!Object.keys(draft).length && !githubToken.trim())} onClick={() => save()}>
            Save
          </button>
        </div>
      </section>
    </>
  );
}
